import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import ApiService from '../services/apiService';
import AddPositionModal from '../components/AddPositionModal';

const formatDateRange = (startDate, endDate, isCurrent) => {
  if (!startDate) return '';
  const parseLocal = (str) => {
    const [year, month, day] = str.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  const start = format(parseLocal(startDate), 'MMM yyyy');
  if (isCurrent) return `${start} — Present`;
  const end = endDate ? format(parseLocal(endDate), 'MMM yyyy') : '';
  return `${start}${end ? ` — ${end}` : ''}`;
};

const PositionCard = ({ position, onEdit, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">{position.position_title}</h3>
            {position.is_current && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-full">
                Current
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-0.5">{position.company_name}</p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
            <span>{formatDateRange(position.start_date, position.end_date, position.is_current)}</span>
            <span>·</span>
            <span>{position.employment_type}</span>
            {position.achievement_count > 0 && (
              <>
                <span>·</span>
                <span>{position.achievement_count} achievement{position.achievement_count !== 1 ? 's' : ''}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {showDeleteConfirm ? (
            <>
              <span className="text-xs text-gray-500 mr-1">Delete?</span>
              <button
                onClick={() => { onDelete(position.id); setShowDeleteConfirm(false); }}
                className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onEdit(position)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                title="Edit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PositionsPage = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);

  const loadPositions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getUserPositions();
      setPositions(data.positions || []);
    } catch (err) {
      setError('Failed to load positions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  const handleOpenAdd = () => {
    setEditingPosition(null);
    setModalOpen(true);
  };

  const handleEdit = (position) => {
    setEditingPosition(position);
    setModalOpen(true);
  };

  const handleDelete = async (positionId) => {
    try {
      await ApiService.deletePosition(positionId);
      setPositions(prev => prev.filter(p => p.id !== positionId));
    } catch (err) {
      setError('Failed to delete position. Please try again.');
    }
  };

  const handleSave = async (formData, positionId) => {
    if (positionId) {
      const updated = await ApiService.updatePosition(positionId, formData);
      setPositions(prev => prev.map(p => p.id === positionId ? (updated.position || updated) : p));
    } else {
      const created = await ApiService.createPosition(formData);
      await loadPositions(); // reload to get sorted order + achievement_count
      void created;
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Positions</h1>
            <p className="text-sm text-gray-500 mt-0.5">Your work history — linked to achievements on your profile</p>
          </div>
          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add position
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">✕</button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-1/2 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/3 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/4" />
              </div>
            ))}
          </div>
        ) : positions.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">No positions yet</h3>
            <p className="text-sm text-gray-500 mb-5 max-w-xs mx-auto">
              Add your work history so achievements can be linked to the right job on your profile.
            </p>
            <button
              onClick={handleOpenAdd}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Add your first position
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {positions.map(position => (
              <PositionCard
                key={position.id}
                position={position}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      <AddPositionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        position={editingPosition}
      />
    </div>
  );
};

export default PositionsPage;
